import api from './api';
import type { AccommodationMandate } from '@/lib/accommodation-mandate';
import type {
  ApiResponse,
  LeadTravel,
  CreateLeadTravelData,
  UpdateLeadTravelData,
} from '@/types';

export interface TravelChecks {
  clinic_visits: number | null;
  over_capacity: boolean;
  missing_identity_document: boolean;
  coordinator_available: boolean;
}

export async function getTravelChecks(leadId: string, arrivalDate?: string): Promise<TravelChecks> {
  const response = await api.get<ApiResponse<TravelChecks>>(`/leads/${leadId}/travel-checks`, {
    params: { arrival_date: arrivalDate || undefined },
  });
  return response.data.data;
}

// Get all travel records for a lead
export const getLeadTravels = async (leadId: string): Promise<LeadTravel[]> => {
  const response = await api.get<ApiResponse<LeadTravel[]>>(`/leads/${leadId}/travel`);
  return response.data.data;
};

// Get a specific travel record
export const getLeadTravel = async (leadId: string, travelId: string): Promise<LeadTravel> => {
  const response = await api.get<ApiResponse<LeadTravel>>(`/leads/${leadId}/travel/${travelId}`);
  return response.data.data;
};

// Create a new travel record
export const createLeadTravel = async (
  leadId: string,
  data: CreateLeadTravelData
): Promise<LeadTravel> => {
  const response = await api.post<ApiResponse<LeadTravel>>(`/leads/${leadId}/travel`, data);
  return response.data.data;
};

// Update a travel record
export const updateLeadTravel = async (
  leadId: string,
  travelId: string,
  data: UpdateLeadTravelData
): Promise<LeadTravel> => {
  const response = await api.put<ApiResponse<LeadTravel>>(
    `/leads/${leadId}/travel/${travelId}`,
    data
  );
  return response.data.data;
};

// Delete a travel record
/**
 * ArtDent 260922 — save the accommodation mandate against a trip. PUT
 * /leads/{lead}/travel/{travel}/accommodation-mandate; 404 where the
 * quote_accommodation_mandate flag is off.
 */
export const saveTravelAccommodationMandate = async (
  leadId: string,
  travelId: string,
  mandate: AccommodationMandate,
): Promise<LeadTravel> => {
  const response = await api.put<ApiResponse<LeadTravel>>(
    `/leads/${leadId}/travel/${travelId}/accommodation-mandate`,
    mandate,
  );
  return response.data.data;
};

export const deleteLeadTravel = async (leadId: string, travelId: string): Promise<void> => {
  await api.delete(`/leads/${leadId}/travel/${travelId}`);
};

/**
 * Radiance 261001 — is the patient's ID card or passport on file? The trip
 * form warns when it is not (DentaDent's ID and passport check, on its own).
 */
export const getIdentityDocumentStatus = async (leadId: string): Promise<{ on_file: boolean }> => {
  const response = await api.get<{ data: { on_file: boolean } }>(`/leads/${leadId}/identity-document`);
  return response.data.data;
};

/**
 * Dentx 261016 — the "Flying from" cities already typed on trips, most used
 * first, one spelling each. The trip form suggests them so the same city is
 * not typed three ways.
 */
export const getOriginCities = async (): Promise<string[]> => {
  const response = await api.get<{ data: string[] }>('/travel/origin-cities');
  return response.data.data;
};
